import {
  type MatchScenario,
  ReconRule,
  createSuggestedAttachment,
} from "@/apis/developer-tools";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPortal,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AlertCircle, Loader2 } from "lucide-react";
import { useState } from "react";

const RECON_RULE_LABELS: Record<ReconRule, string> = {
  [ReconRule.SORT_OUT]: "Sort Out",
  [ReconRule.AMOUNT_REFERENCE]: "Amount Reference",
  [ReconRule.BATCH_PAYMENT]: "Batch Payment",
  [ReconRule.SUPPLIER_MATCH]: "Supplier Match",
  [ReconRule.AMOUNT]: "Amount",
  [ReconRule.AI_CONFIRMATION]: "AI Confirmation",
  [ReconRule.AI_RECON]: "AI Recon",
  [ReconRule.USER_MATCH]: "User Match",
};

const MATCH_SCENARIO_LABELS: Record<MatchScenario, string> = {
  exact: "Exact Match",
  discount_1: "1% Skonto Discount",
  discount_2: "2% Skonto Discount",
  discount_3: "3% Skonto Discount",
  foreign_currency: "Foreign Currency",
};

const MATCH_SCENARIO_DESCRIPTIONS: Record<MatchScenario, string> = {
  exact: "Invoice amount = Transaction amount",
  discount_1: "Invoice amount with 1% Skonto discount",
  discount_2: "Invoice amount with 2% Skonto discount",
  discount_3: "Invoice amount with 3% Skonto discount",
  foreign_currency: "Invoice in foreign currency, transaction in EUR",
};

type Props = {
  isDialogOpen: boolean;
  setIsDialogOpen: (isDialogOpen: boolean) => void;
};

export const TriggerTransactionSuggestions = ({
  isDialogOpen,
  setIsDialogOpen,
}: Props) => {
  const [scenario, setScenario] = useState<MatchScenario | undefined>(
    undefined,
  );
  const [transactionAmountCents, setTransactionAmountCents] = useState<
    string | undefined
  >(undefined);
  const [invoiceCurrency, setInvoiceCurrency] = useState<string>("USD");
  const [confidence, setConfidence] = useState<0 | 1 | undefined>(undefined);
  const [rule, setRule] = useState<ReconRule | undefined>(undefined);
  const [supplierName, setSupplierName] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirmAction = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const amountCents = transactionAmountCents
        ? Number.parseInt(transactionAmountCents, 10)
        : undefined;

      await createSuggestedAttachment({
        scenario,
        transactionAmountCents:
          amountCents && !Number.isNaN(amountCents) ? amountCents : undefined,
        invoiceCurrency:
          scenario === "foreign_currency" ? invoiceCurrency : undefined,
        confidence,
        rule,
        supplierName: supplierName || undefined,
      });
      setIsDialogOpen(false);
      resetForm();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setScenario(undefined);
    setTransactionAmountCents(undefined);
    setInvoiceCurrency("USD");
    setConfidence(undefined);
    setRule(undefined);
    setSupplierName("");
    setError(null);
  };

  const handleDialogClose = (open: boolean) => {
    setIsDialogOpen(open);
    if (!open) {
      resetForm();
    }
  };

  return (
    <Dialog open={isDialogOpen} onOpenChange={handleDialogClose}>
      <DialogPortal container={document.body}>
        <DialogContent
          className="z-[9999] max-w-lg"
          onPointerDownOutside={(e) => {
            e.preventDefault();
            handleDialogClose(false);
          }}
          onEscapeKeyDown={() => {
            handleDialogClose(false);
          }}
        >
          <DialogHeader>
            <DialogTitle>Create Suggested Attachment</DialogTitle>
            <DialogDescription>
              Create a test invoice and bank transaction with a suggested match
              for testing reconciliation flows.
            </DialogDescription>
          </DialogHeader>

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="grid gap-4 py-2">
            <div className="flex flex-col gap-2">
              <Label htmlFor="scenario">Match Scenario</Label>
              <Select
                value={scenario}
                onValueChange={(value) => setScenario(value as MatchScenario)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select scenario (default: exact)" />
                </SelectTrigger>
                <SelectContent className="z-[10000]">
                  {Object.entries(MATCH_SCENARIO_LABELS).map(
                    ([value, label]) => (
                      <SelectItem key={value} value={value}>
                        <div className="flex flex-col">
                          <span>{label}</span>
                          <span className="text-xs text-muted-foreground">
                            {
                              MATCH_SCENARIO_DESCRIPTIONS[
                                value as MatchScenario
                              ]
                            }
                          </span>
                        </div>
                      </SelectItem>
                    ),
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="transaction-amount">
                Transaction Amount (cents){" "}
                <span className="text-muted-foreground font-normal">
                  (optional)
                </span>
              </Label>
              <Input
                id="transaction-amount"
                type="number"
                placeholder="e.g., 50000 (= 500€). Default: random 100€-1000€"
                autoFocus={false}
                value={transactionAmountCents ?? ""}
                onChange={(e) =>
                  setTransactionAmountCents(e.target.value || undefined)
                }
              />
            </div>

            {scenario === "foreign_currency" && (
              <div className="flex flex-col gap-2">
                <Label htmlFor="invoice-currency">Invoice Currency</Label>
                <Input
                  id="invoice-currency"
                  type="text"
                  placeholder="e.g., USD, GBP, CHF"
                  autoFocus={false}
                  value={invoiceCurrency}
                  onChange={(e) =>
                    setInvoiceCurrency(e.target.value.toUpperCase())
                  }
                />
              </div>
            )}

            <div className="flex flex-col gap-2">
              <Label htmlFor="rule">Reconciliation Rule</Label>
              <Select
                value={rule}
                onValueChange={(value) => setRule(value as ReconRule)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select rule (default: USER_MATCH)" />
                </SelectTrigger>
                <SelectContent className="z-[10000]">
                  {Object.entries(RECON_RULE_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="confidence">Confidence</Label>
              <Select
                value={confidence?.toString()}
                onValueChange={(value) =>
                  setConfidence(value ? (Number(value) as 0 | 1) : undefined)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select confidence (default: based on rule)" />
                </SelectTrigger>
                <SelectContent className="z-[10000]">
                  <SelectItem value="1">
                    <div className="flex flex-col">
                      <span>1 - Zero-touch (auto-approved)</span>
                      <span className="text-xs text-muted-foreground">
                        Used by AMOUNT_REFERENCE, SUPPLIER_MATCH, BATCH_PAYMENT,
                        AI_CONFIRMATION
                      </span>
                    </div>
                  </SelectItem>
                  <SelectItem value="0">
                    <div className="flex flex-col">
                      <span>0 - Suggested (requires review)</span>
                      <span className="text-xs text-muted-foreground">
                        Used by AI_RECON
                      </span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="supplier-name">
                Supplier Name{" "}
                <span className="text-muted-foreground font-normal">
                  (optional)
                </span>
              </Label>
              <Input
                id="supplier-name"
                type="text"
                placeholder="Custom supplier name for the invoice"
                autoFocus={false}
                value={supplierName}
                onChange={(e) => setSupplierName(e.target.value)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => handleDialogClose(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button onClick={handleConfirmAction} disabled={isLoading}>
              {isLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Create Suggestion
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
};
